import { useRef, useState } from 'react';
import type { FocusEventHandler, ReactNode } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { mergeStyles } from 'react-select';
import type {
  CSSObjectWithLabel,
  FormatOptionLabelMeta,
  GroupBase,
  SelectComponentsConfig,
  StylesConfig,
} from 'react-select';

import { ErrorType, Option, RegisterType } from 'types';

import { SelectMotives, pickMotive } from 'data/selectMotives.data';

import { Label } from 'components/Label';

import { useSelectWithControl } from './SelectWithControl.hooks';
import * as S from './SelectWithControl.styles';
import { StyledSelectProps } from './SelectWithControl.styles';

export type SelectProps = {
  name: string;
  options: Option[];
  label?: string;
  defaultValue?: string | number;
  control: Control;
  motive?: SelectMotives;
  disabled?: boolean;
  onInputChange?: (arg: string) => void;
  onSelect?: (arg: Option) => void;
  onBlur?: FocusEventHandler<HTMLElement>;
  error?: ErrorType;
  required?: boolean;
  className?: string;
  value?: any; // eslint-disable-line
  asKeyValues?: boolean;
  isMulti?: boolean;
  ref?: RegisterType;
  placeholder?: string;
  isSearchable?: boolean;
  hideEmptyOption?: boolean;
  /** Translated label for the empty option (default: hardcoded `Select...`). */
  emptyOptionLabel?: string;
  /** Render the option differently in the menu and in the selected value (`meta.context`). */
  formatOptionLabel?: (option: Option, meta: FormatOptionLabelMeta<Option>) => ReactNode;
  /** react-select component overrides (e.g. a custom `DropdownIndicator`). */
  components?: SelectComponentsConfig<Option, boolean, GroupBase<Option>>;
  /**
   * Menu as wide as the control, growing to fit longer option labels up to this width
   * (and never past the right edge of the viewport; longer labels wrap).
   */
  menuMaxWidth?: string;
  /** Options are being fetched: the menu says so instead of "no options". */
  isLoading?: boolean;
  /**
   * Colour (CSS value) of the focused border and of the hovered / selected option instead of
   * the motive's blue (e.g. the catalogue colour in the New / Copy Order modals).
   */
  accentColor?: string;
} & StyledSelectProps;

/** Space kept between a `menuMaxWidth` menu and the right edge of the viewport (px). */
const MENU_VIEWPORT_GUTTER = 16;

export function SelectWithControl(props: SelectProps): React.ReactElement {
  const {
    label = undefined,
    control,
    defaultValue,
    motive = 'default',
    name,
    disabled = false,
    onInputChange = undefined,
    onSelect = undefined,
    onBlur = undefined,
    error = undefined,
    scale,
    required = false,
    className = undefined,
    value = undefined,
    asKeyValues = false,
    isMulti = false,
    placeholder: placeholderFromProps,
    isSearchable = true,
    formatOptionLabel,
    components,
    menuMaxWidth,
    isLoading = false,
    accentColor,
  } = props;

  const { t } = useTranslation();
  const placeholder = placeholderFromProps ?? t('common.selectPlaceholder');
  const { selectedValue, selectedValueSet, optionsFiltered } = useSelectWithControl(props);

  const containerRef = useRef<HTMLDivElement>(null);
  // Width left between the select and the viewport's right edge, measured when the menu opens.
  const [menuRoom, menuRoomSet] = useState<number | null>(null);
  const measureMenuRoom = () => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) menuRoomSet(Math.max(window.innerWidth - rect.left - MENU_VIEWPORT_GUTTER, 0));
  };

  const baseStyles = pickMotive(motive) as StylesConfig;
  const motiveStyles = accentColor
    ? mergeStyles(baseStyles, {
        control: (provided, state) =>
          ({
            ...provided,
            ...(state.isFocused && {
              borderColor: accentColor,
              boxShadow: `0 0 0 0.1rem ${accentColor}`,
              '&:hover': { borderColor: accentColor },
            }),
          } as CSSObjectWithLabel),
        option: (provided, state) =>
          ({
            ...provided,
            backgroundColor: state.isSelected || state.isFocused ? accentColor : '#ffffff',
            color: state.isSelected || state.isFocused ? '#ffffff' : provided.color,
            '&:hover': { backgroundColor: accentColor, color: '#ffffff' },
            '&:active': { backgroundColor: accentColor, color: '#ffffff' },
          } as CSSObjectWithLabel),
      })
    : baseStyles;
  const styles = menuMaxWidth
    ? mergeStyles(motiveStyles, {
        menu: (provided) =>
          ({
            ...provided,
            width: 'max-content',
            minWidth: '100%',
            maxWidth: menuRoom === null ? menuMaxWidth : `min(${menuMaxWidth}, ${menuRoom}px)`,
          } as CSSObjectWithLabel),
      })
    : motiveStyles;
  // `S.Select` is `styled(ReactSelect)`, typed with `unknown` options.
  const optionLabelFormatter = formatOptionLabel as
    | ((option: unknown, meta: FormatOptionLabelMeta<unknown>) => ReactNode)
    | undefined;
  const selectComponents = components as
    | SelectComponentsConfig<unknown, boolean, GroupBase<unknown>>
    | undefined;

  return (
    <S.Container ref={containerRef} scale={scale} className={className} motive={motive}>
      {label ? (
        <Label htmlFor={name} required={required} error={error}>
          {label}
        </Label>
      ) : null}

      <Controller
        control={control}
        defaultValue={defaultValue ?? value}
        name={name}
        rules={{
          required: required ? t('form.pleaseSelect', { label: label ?? '' }) : false,
          validate: (val) =>
            val?.key !== '' ? undefined : t('form.pleaseSelect', { label: label ?? '' }),
        }}
        render={({ field: { onChange, ref } }) => {
          // eslint-disable-next-line
          const setOnChange = (val: any) => {
            onChange(asKeyValues ? { key: val.value } : val.value);
            selectedValueSet(val);

            if (onSelect) {
              onSelect(val);
            }
          };

          return onInputChange ? (
            <S.Select
              styles={styles}
              ref={ref}
              value={selectedValue}
              isDisabled={disabled}
              options={optionsFiltered}
              onChange={(val: unknown) => setOnChange(val)}
              onInputChange={(e) => onInputChange(e)}
              onBlur={onBlur}
              isMulti={isMulti}
              placeholder={placeholder}
              isSearchable={isSearchable}
              noOptionsMessage={() => t('common.noOptions')}
              isLoading={isLoading}
              loadingMessage={() => t('common.searching')}
              onMenuOpen={menuMaxWidth ? measureMenuRoom : undefined}
              formatOptionLabel={optionLabelFormatter}
              components={selectComponents}
            />
          ) : (
            <S.Select
              styles={styles}
              ref={ref}
              value={selectedValue}
              isDisabled={disabled}
              options={optionsFiltered}
              onChange={(val: unknown) => setOnChange(val)}
              onBlur={onBlur}
              isMulti={isMulti}
              placeholder={placeholder}
              isSearchable={isSearchable}
              noOptionsMessage={() => t('common.noOptions')}
              isLoading={isLoading}
              loadingMessage={() => t('common.searching')}
              onMenuOpen={menuMaxWidth ? measureMenuRoom : undefined}
              formatOptionLabel={optionLabelFormatter}
              components={selectComponents}
            />
          );
        }}
      />
    </S.Container>
  );
}
